import EngineeringIcon from "@mui/icons-material/Engineering";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import HikingIcon from "@mui/icons-material/Hiking";
import ScienceIcon from "@mui/icons-material/Science";
import { useTheme } from "@mui/material";

import { NavSection } from "../../components/LeftNavV2/LeftNavDrawerV2";
import { type GroupChildNavItem } from "../../components/LeftNavV2/NavItemV2";

function staffChild(
  text: string,
  path: string,
  textColor: string,
  description?: string
): GroupChildNavItem {
  return {
    kind: "GroupChild",
    text,
    path,
    textColor,
    description,
  };
}

export function useStaffNavSectionsV2(): NavSection[] {
  const theme = useTheme();
  const staffColor = theme.palette.warning.light;

  const customersChildren: GroupChildNavItem[] = [
    staffChild(
      "Organizations",
      "staff/implementation",
      staffColor,
      "Manage organization setup, implementation details, and account context."
    ),
    staffChild(
      "Payments",
      "staff/payments",
      staffColor,
      "Review payment records and reconcile report-level transactions."
    ),
    staffChild(
      "Subscriptions",
      "staff/subscription-overview",
      staffColor,
      "Compare SFDC contracted subscription tiers and facility counts against Encamp actuals."
    ),
    staffChild(
      "Feature Flags",
      "staff/feature-flags",
      staffColor,
      "Manage Encamp-managed feature flags across organizations."
    ),
    staffChild(
      "Roles",
      "staff/roles",
      staffColor,
      "Edit the permissions of an organization's roles, and create or remove roles."
    ),
    staffChild("Insights Demo", "staff/insights-demo-org-creator", staffColor),
  ];

  const complianceChildren: GroupChildNavItem[] = [
    staffChild(
      "Fulfillment",
      "staff/fulfillment",
      staffColor,
      "Monitor report progress, assignments, and workflow outcomes."
    ),
    staffChild(
      "Generator Statuses",
      "staff/generator-status-changes",
      staffColor,
      "Monitor potential generator status discrepancies across facilities."
    ),
    staffChild("Requirements", "staff/requirements", staffColor),
    staffChild(
      "Agencies",
      "staff/agencies",
      staffColor,
      "Maintain LEPC, fire department, and state agency records."
    ),
    staffChild(
      "Mail Room",
      "staff/mail-room",
      staffColor,
      "Create and track customer communications and delivery status."
    ),
    staffChild(
      "Credentials",
      "staff/credentials",
      staffColor,
      "Manage EPCRA, RCRAInfo, and STEERS credential implementations."
    ),
  ];

  const platformChildren: GroupChildNavItem[] = [
    staffChild(
      "Observe",
      "staff/observe",
      staffColor,
      "Monitor Scout diagnostics and run Scout load tests."
    ),
    staffChild("Jobs", "staff/jobs", staffColor),
    staffChild("Pipelines", "staff/pipelines", staffColor),
    staffChild("Workflow Config", "staff/workflow-config", staffColor),
    staffChild("Tags", "staff/tags", staffColor),
  ];

  const previewChildren: GroupChildNavItem[] = [
    staffChild("Notifications", "staff/notifications", staffColor),
    staffChild(
      "Amazon WADM",
      "staff/amazon-wadm",
      staffColor,
      "Search and export Amazon waste generation records by site, profile, and shipment."
    ),
  ];

  return [
    {
      title: "Staff",
      textColor: staffColor,
      items: [
        {
          kind: "Group",
          text: "Customers",
          description: "Organizations, payments, subscriptions, and demo tooling.",
          Icon: GroupsRoundedIcon,
          path: "staff/implementation",
          isExpandable: true,
          defaultExpanded: true,
          textColor: staffColor,
          children: customersChildren,
        },
        {
          kind: "Group",
          text: "Compliance",
          description: "Fulfillment, requirements, agencies, mail, and credentials.",
          Icon: HikingIcon,
          path: "staff/fulfillment",
          isExpandable: true,
          defaultExpanded: true,
          textColor: staffColor,
          children: complianceChildren,
        },
        {
          kind: "Group",
          text: "Platform",
          description: "Observability, jobs, pipelines, workflow config, and tags.",
          Icon: EngineeringIcon,
          path: "staff/observe",
          isExpandable: true,
          defaultExpanded: true,
          textColor: staffColor,
          children: platformChildren,
        },
        {
          kind: "Group",
          text: "Preview",
          description: "Experimental staff tools that are still in preview.",
          Icon: ScienceIcon,
          path: "staff/notifications",
          isExpandable: true,
          defaultExpanded: true,
          textColor: staffColor,
          children: previewChildren,
        },
      ],
    },
  ];
}
